import { BrandRetailerStatus } from "__generated__/gql/graphql";
import { AppliedFilter } from "components/Shared/FilterEditor/utils";
import TabListArray from "components/Shared/TabListArray";
import { BRAND_RETAILER_STATUS_LABELS } from "components/Shared/BrandRetailerStatus/utils";
import { useCallback, useMemo } from "react";

export const STATUS_TABS_FILTER_NAME = "statusTab";

export enum StatusFilter {
  ALL = "ALL",
  ACTIVE = "ACTIVE",
  CURRENT = "CURRENT",
  APPROVED = "APPROVED",
  PROSPECTIVE = "PROSPECTIVE",
}

const STATUS_OPTIONS = [
  StatusFilter.ALL,
  StatusFilter.ACTIVE,
  StatusFilter.CURRENT,
  StatusFilter.APPROVED,
  StatusFilter.PROSPECTIVE,
];

export const SINGLE_STATUS_OPTIONS: Partial<
  Record<StatusFilter, BrandRetailerStatus>
> = {
  [StatusFilter.CURRENT]: BrandRetailerStatus.CURRENT,
  [StatusFilter.APPROVED]: BrandRetailerStatus.APPROVED,
  [StatusFilter.PROSPECTIVE]: BrandRetailerStatus.PROSPECTIVE,
};

const STATUS_LABELS: Partial<Record<StatusFilter, string>> = {
  [StatusFilter.ALL]: "All",
  [StatusFilter.ACTIVE]: "Active",
};

type Props = {
  filters: AppliedFilter[];
  handleFiltersChange: (filters: AppliedFilter[]) => void;
};

export default function StatusFilterTabs({
  filters,
  handleFiltersChange,
}: Props) {
  const selected = useMemo(() => {
    const appliedFilter = filters.find(
      (f) => f.filter === STATUS_TABS_FILTER_NAME,
    );

    if (!appliedFilter) return StatusFilter.ALL;

    if (
      typeof appliedFilter.value !== "string" ||
      appliedFilter.variant !== "equalTo"
    ) {
      return;
    }

    return appliedFilter.value as StatusFilter;
  }, [filters]);

  const handleChange = useCallback(
    (value: StatusFilter | null) => {
      const otherFilters = filters.filter(
        (f) => f.filter !== STATUS_TABS_FILTER_NAME,
      );
      handleFiltersChange(
        !value
          ? otherFilters
          : [
              ...otherFilters,
              {
                filter: STATUS_TABS_FILTER_NAME,
                value,
                variant: "equalTo",
              },
            ],
      );
    },
    [filters, handleFiltersChange],
  );

  return (
    <TabListArray
      items={STATUS_OPTIONS}
      selected={selected}
      onChange={handleChange}
      getItemLabel={(statusFilter) => {
        if (statusFilter === null || statusFilter === undefined) {
          return null;
        }
        const singleStatus = SINGLE_STATUS_OPTIONS[statusFilter];
        if (singleStatus) {
          return BRAND_RETAILER_STATUS_LABELS[singleStatus];
        }
        return STATUS_LABELS[statusFilter];
      }}
    />
  );
}
